// (c) 2023 ABBYY Development, Inc.

import { lazy, Suspense, FC } from 'react';
import styled from 'styled-components';
import {
    BrowserRouter as Router,
    Prompt,
    Redirect,
    Route,
    Switch
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Header,
    NavigationDrawer,
    NavLink as NavigationDrawerLink,
    NotFoundError,
    UploadEventListMenu,
    ConfirmNavigation,
    ProductList,
    FolderLog,
    SettingsOutline,
    ViewSynchro,
    InfoFill,
    ViewMonitor,
    LoadingIndicator,
    Network
} from 'components';
import { useBoolean } from 'hooks';
import { TemplatesRouter } from './domain/templates/TemplatesRouter';
import { useBlockNavigation, useSidenavState } from './providers';
import * as palette from './config';
import {
    List,
    Title as AppTitle,
    NavItem
} from './components/navigation-drawer/Styled';
import { Container, Title } from 'components/navigation-drawer/NavLink/Styled';
import { AboutDialog } from 'domain/about-dialog/About.dialog';
import UserMenu from 'domain/user-menu';

const MainContainer = styled.div`
    height: calc(100vh - ${palette.HEADER_SIZE});
    display: flex;
    max-width: 100vw;
`;

const RecorderListContainer = lazy(
    () => import('./domain/recorders/RecorderListContainer')
);

const LogsRouter = lazy(() => import('./domain/logs/LogsRouter'));

const Settings = lazy(() => import('./domain/settings/Settings'));

const Transformations = lazy(
    () => import('./domain/transformations/TransformationListContainer')
);

const Agents = lazy(() => import('./domain/agents/AgentListContainer'));

const App: FC<{}> = () => {
    const { t } = useTranslation();
    const { isNarrow } = useSidenavState();
    const { setCallback, showBlockModal, isNavigationBlocked } =
        useBlockNavigation();
    const [isAboutDialogOpen, showAboutDialog, hideAboutDialog] = useBoolean();

    return (
        <Router
            getUserConfirmation={(_, callback) => {
                setCallback(() => callback);
                showBlockModal();
            }}
        >
            <Prompt when={isNavigationBlocked} message={''} />
            <ConfirmNavigation />
            <AboutDialog isOpen={isAboutDialogOpen} close={hideAboutDialog} />
            <Header>
                <UploadEventListMenu />
            </Header>
            <MainContainer>
                <NavigationDrawer>
                    <AppTitle>
                        {isNarrow
                            ? t(
                                  'views.applications.recording_service.short_title'
                              )
                            : t('views.applications.recording_service.title')}
                    </AppTitle>
                    <List>
                        <NavigationDrawerLink
                            to="/templates"
                            title={t('general.Templates')}
                            Icon={ProductList}
                        />
                        <NavigationDrawerLink
                            to="/recorders"
                            title={t('general.Recorders')}
                            Icon={ViewMonitor}
                        />
                        <NavigationDrawerLink
                            to="/logs"
                            title={t('general.Logs')}
                            Icon={FolderLog}
                        />
                        <NavigationDrawerLink
                            to="/transformations"
                            title={t('general.Transformations')}
                            Icon={ViewSynchro}
                        />
                        <NavigationDrawerLink
                            to="/agents"
                            title={t('general.Agents')}
                            Icon={Network}
                        />
                        <NavigationDrawerLink
                            to="/settings"
                            title={t('general.Settings')}
                            Icon={SettingsOutline}
                        />

                        <UserMenu />
                        <NavItem onClick={showAboutDialog}>
                            <Container>
                                <InfoFill />
                                <Title>{t('general.About')}</Title>
                            </Container>
                        </NavItem>
                    </List>
                </NavigationDrawer>
                <Suspense fallback={<LoadingIndicator show />}>
                    <Switch>
                        <Route path="/templates">
                            <TemplatesRouter />
                        </Route>
                        <Route path="/recorders">
                            <RecorderListContainer />
                        </Route>
                        <Route path="/logs">
                            <LogsRouter />
                        </Route>
                        <Route exact path="/settings">
                            <Settings />
                        </Route>
                        <Route path="/transformations">
                            <Transformations />
                        </Route>
                        <Route exact path="/agents">
                            <Agents />
                        </Route>
                        <Route exact path="/">
                            <Redirect to="/templates" />
                        </Route>
                        <Route path="*">
                            <NotFoundError />
                        </Route>
                    </Switch>
                </Suspense>
            </MainContainer>
        </Router>
    );
};

export default App;
